import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefCallback,
  type RefObject,
} from 'react';

/** @internal */
export type ObservedSize = {
  width: number | undefined;
  height: number | undefined;
};

type UseResizeObserverOptions<T extends Element> = {
  ref?: RefObject<T | null> | T | null;
  onResize?: (size: ObservedSize) => void;
  box?: ResizeObserverBoxOptions;
};

type UseResizeObserverResult<T extends Element> = ObservedSize & {
  ref: RefCallback<T | null>;
};

type SubscriberCleanup = () => void;

// Resolves the element to observe from either a callback ref element, an
// external element reference, or an external RefObject.
function resolveElement<T extends Element>(
  cbElement: T | null,
  refOrElement: RefObject<T | null> | T | null | undefined,
): T | null {
  if (cbElement) {
    return cbElement;
  }
  if (!refOrElement) {
    return null;
  }
  if (refOrElement instanceof Element) {
    return refOrElement;
  }
  return (refOrElement as RefObject<T | null>).current;
}

type BoxProp = 'borderBoxSize' | 'contentBoxSize' | 'devicePixelContentBoxSize';

function extractSize(
  entry: ResizeObserverEntry,
  boxProp: BoxProp,
  sizeType: keyof ResizeObserverSize,
): number | undefined {
  if (!entry[boxProp]) {
    if (boxProp === 'contentBoxSize') {
      return entry.contentRect[sizeType === 'inlineSize' ? 'width' : 'height'];
    }
    return undefined;
  }
  // The spec exposes sizes as FrozenArray; some older browsers returned an object
  const sizes = entry[boxProp] as ResizeObserverSize[] | ResizeObserverSize;
  return Array.isArray(sizes)
    ? sizes[0]?.[sizeType]
    : (sizes as ResizeObserverSize)[sizeType];
}

/**
 * Observes an element's size using the native `ResizeObserver` API and returns
 * its dimensions. Accepts either a React ref or a direct element reference via
 * options; when neither is provided the returned `ref` callback should be
 * attached to the target element.
 *
 * Behaviour matches the `use-resize-observer` npm package: when `onResize` is
 * provided the hook skips state updates to avoid unnecessary re-renders.
 * @internal
 */
export function useResizeObserver<T extends Element = HTMLElement>(
  options: UseResizeObserverOptions<T> = {},
): UseResizeObserverResult<T> {
  const onResizeRef = useRef(options.onResize);
  onResizeRef.current = options.onResize;

  const [size, setSize] = useState<ObservedSize>({
    width: undefined,
    height: undefined,
  });

  // Guard against calling setSize after unmount (ResizeObserver may fire late).
  const didUnmount = useRef(false);
  useEffect(() => {
    didUnmount.current = false;
    return () => {
      didUnmount.current = true;
    };
  }, []);

  // Track previous dimensions to skip no-op updates.
  const previous = useRef<ObservedSize>({
    width: undefined,
    height: undefined,
  });

  // Tracks the last element we subscribed to and its cleanup function.
  const lastReport = useRef<{
    element: Element | null;
    subscriber: ((element: Element) => SubscriberCleanup) | null;
    cleanup?: SubscriberCleanup;
  } | null>(null);

  // Element from the returned ref callback (takes priority over options.ref).
  const cbComponentRef = useRef<T | null>(null);

  // Store options.ref in a ref so evaluateSubscription reads it fresh each time,
  // matching the original library's behaviour of re-checking after every render.
  const refOrComponentRef = useRef(options.ref);
  refOrComponentRef.current = options.ref;

  // The subscriber is re-created when the box option changes, which triggers a
  // re-subscription in evaluateSubscription via reference comparison.
  const subscriber = useCallback(
    (element: Element): SubscriberCleanup => {
      let boxProp: BoxProp = 'contentBoxSize';
      if (options.box === 'border-box') {
        boxProp = 'borderBoxSize';
      } else if (options.box === 'device-pixel-content-box') {
        boxProp = 'devicePixelContentBoxSize';
      }

      const ro = new ResizeObserver((entries) => {
        const entry = entries[0];

        const newWidth = extractSize(entry, boxProp, 'inlineSize');
        const newHeight = extractSize(entry, boxProp, 'blockSize');

        const roundedWidth =
          newWidth === undefined ? undefined : Math.round(newWidth);
        const roundedHeight =
          newHeight === undefined ? undefined : Math.round(newHeight);

        if (
          previous.current.width !== roundedWidth ||
          previous.current.height !== roundedHeight
        ) {
          const newSize = { width: roundedWidth, height: roundedHeight };
          previous.current = newSize;
          if (onResizeRef.current) {
            onResizeRef.current(newSize);
          } else if (!didUnmount.current) {
            setSize(newSize);
          }
        }
      });

      ro.observe(element, { box: options.box });
      return () => ro.unobserve(element);
    },
    [options.box],
  );

  const evaluateSubscription = useCallback(() => {
    const element = resolveElement(
      cbComponentRef.current,
      refOrComponentRef.current,
    );

    if (
      lastReport?.current?.element === element &&
      lastReport?.current?.subscriber === subscriber
    ) {
      return;
    }

    lastReport.current?.cleanup?.();
    lastReport.current = {
      element,
      subscriber,
      cleanup: element ? subscriber(element) : undefined,
    };
  }, [subscriber]);

  // Run after every render with no dependency array — this is intentional and
  // mirrors the original library. It ensures that changes to a RefObject's
  // `.current` (which React sets after commit, not during render) are picked up
  // on the very next render cycle without requiring an extra state update.
  useEffect(() => {
    evaluateSubscription();
  });

  // Cleanup the current subscription on unmount.
  useEffect(() => {
    return () => {
      lastReport.current?.cleanup?.();
      lastReport.current = null;
    };
  }, []);

  const refCallback: RefCallback<T | null> = useCallback(
    (element) => {
      cbComponentRef.current = element;
      evaluateSubscription();
    },
    [evaluateSubscription],
  );

  return useMemo(
    () => ({
      ref: refCallback,
      width: size.width,
      height: size.height,
    }),
    [refCallback, size.width, size.height],
  );
}
